import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, InfiniteRowModelModule } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { useState, useMemo, useCallback } from "react";
import ActionCellBilling from "./Utility/ActionCellBilling";
import CustomHeader from "../Utility/CustomHeader";
import ActionCellStock from "../Utility/ActionCellStock";
import PharmacyStock from "./PharmacyStock";
import { Offcanvas } from "react-bootstrap";

ModuleRegistry.registerModules([InfiniteRowModelModule]);
function StockComponentTable({
  getPatients,
  isFetching,
  isLoading,
  isSuccess,
  gridRef,
  selectedFilters,
  hasData,
  setHasData,
  onEdit,
  onAddNew, 
}) {

  const navigate = useNavigate();
  const [showOffcanvas, setShowOffcanvas] = useState(false);

 const handleView = (data) => {
  setShowOffcanvas(true);
};

const handleEditItem = (data) => {
  if (onEdit) onEdit(data); // call parent handler
};


  const getStableColor = (key) => {
    const bgColors = [
      "bg-danger bg-opacity-50",
      "bg-success bg-opacity-50",
      "bg-primary bg-opacity-50",
      "bg-warning bg-opacity-50",
      "bg-secondary bg-opacity-50",
      "bg-danger bg-opacity-50",
      "bg-primary bg-opacity-50",
      "bg-info bg-opacity-50",
      "bg-warning bg-opacity-50",
      "bg-success bg-opacity-50",
      "bg-info bg-opacity-10",
      "bg-success bg-opacity-50",
      "bg-danger bg-opacity-50",
      "bg-danger bg-opacity-50",
      "bg-secondary bg-opacity-50",
    ];
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % bgColors.length;
    return bgColors[index];
  };

  // 10 static demo rows (based on provided shape)
const staticRows = useMemo(
  () => [
    {
      productName: "Doplus Dt Tab.",
      manufacturer: "Orgyn Laboratories Pvt Ltd",
      price: "₹73.13",
      availability: "In Stock",
      category:"Medication",
      expiry: "04/27",
      form: "Tablet",
    },
    {
      productName: "Crocin Pain Relief",
      manufacturer: "ABC Pharma Pvt Ltd",
      price: "₹45.00",
      availability: "In Stock",
      category:"Mother&Baby Care",
      expiry: "09/26",
      form: "Syrup",
    },
  ],
  []
);



  const statusColor = (status) => {
    switch (status) {
      case "Accepted":
        return { background: "#194787", border: "#194787" };
      case "Delivered":
        return { background: "#198754", border: "#198754" };
      case "Pending":
        return { background: "#ffc107", border: "#ffc107" };
      case "Cancelled":
        return { background: "#dc3545", border: "#dc3545" };
      default:
        return { background: "#6c757d", border: "#6c757d" };
    }
  };

  const fulfillmentColor = (status) => {
    return { background: "#198754", border: "#198754" }; // Delivery = green pill
  };

  const billingColor = (status) => {
    return { background: "#198754", border: "#198754" }; // Fully Ready = green pill
  };

  const onGridReady = useCallback(
    (params) => {
      const dataSource = {
        rowCount: undefined,
        getRows: (getRowsParams) => {
          const { startRow, endRow } = getRowsParams;
          const rowsThisPage = staticRows.slice(startRow, endRow);
          const lastRow = endRow >= staticRows.length ? staticRows.length : -1;

          if (typeof setHasData === "function") {
            setHasData(staticRows.length > 0);
          }

          getRowsParams.successCallback(rowsThisPage, lastRow);
        },
      };

      params.api.setGridOption("datasource", dataSource);
    },
    [setHasData, staticRows]
  );

const [colDefs] = useState([
  {
    headerName: "Product Name",
    field: "productName",
    width: 220,
    minWidth: 250,
    sortable: true,
    filter: true,
    cellRenderer: (params) => {
      if (!params.value) return null;
      const name = params.value;
      const initial = name.charAt(0).toUpperCase();
      const bgColor = getStableColor(name);
      return (
        <div className="d-flex align-items-center justify-content-center gap-3">
          <div
            className={`d-flex justify-content-center align-items-center rounded-circle text-white fw-bold ${bgColor}`}
            style={{ width: "2rem", height: "2rem", fontSize: "0.875rem" }}
          >
            {initial}
          </div>
          <div className="d-flex flex-column justify-content-center align-items-center">
            <span className="fw-bold text-secondary small">{name}</span>
            <span className="text-secondary fw-medium small">
              {params.data?.manufacturer}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    headerName: "Price",
    field: "price",
    minWidth: 60,
    sortable: true,
    filter: true,
    cellRenderer: (params) => (
      <span className="text-secondary fw-medium small">{params.value}</span>
    ),
  },
  {
    headerName: "Availability",
    field: "availability",
    minWidth: 120,
    cellRenderer: (params) => {
      const isInStock = params.value === "In Stock";
      return (
        <div
          className={`d-inline-block text-center px-3 py-1 rounded-pill fw-semibold ${
            isInStock ? "text-white" : "text-dark"
          }`}
          style={{
            fontSize: "12px",
            backgroundColor: isInStock ? "#198754" : "#dc3545",
          }}
        >
          {params.value}
        </div>
      );
    },
  },
  {
    headerName: "Category",
    field: "category",
    minWidth: 140,
    sortable: true,
    filter: true,
    cellRenderer: (params) => (
      <span className="text-secondary fw-medium small">{params.value}</span>
    ),
  },
  {
    headerName: "Expiry",
    field: "expiry",
    minWidth: 100,
    sortable: true,
    filter: true,
    cellRenderer: (params) => (
      <span className="text-secondary fw-medium small">{params.value}</span>
    ),
  },
  {
    headerName: "Form",
    field: "form",
    minWidth: 100,
    cellRenderer: (params) => (
      <span className="text-secondary fw-medium small">{params.value}</span>
    ),
  },
 {
  headerName: "Action",
  field: "",
  minWidth: 120,
  resizable: false,
  cellStyle: { display: "flex", justifyContent: "center", alignItems: "center" },
  cellRenderer: (params) =>
    params.data ? (
      <ActionCellStock
        data={params.data}
        onView={handleView}
        onEdit={handleEditItem}
         onAddNew={onAddNew}
      />
    ) : null,
},



]);


  const autoSizeStrategy = {
    type: "fitGridWidth",
    columnLimits: [
      {
        colId: "No of Team Members",
        minWidth: 900,
      },
    ],
  };

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      filter: true,
      sortable: true,
      enableRowGroup: true,
      sort: "asc",
      resizable: true,
      headerComponent: CustomHeader,
    };
  }, []);

  const tableStyle = `

  .btn-IdWmkZEQJd {
  border: none;
    background: none;
    color: #035ecf;
    width: 2.4rem;
    height: 2.4rem;
    display: flex
;
    align-items: center;
    justify-content: center;
    border-radius: .245rem;
    padding: .25rem .5rem;
  }


.ag-horizontal-right-spacer.ag-scroller-corner {
    display: none !important;
}

.ag-header-viewport {
  overflow-x: hidden !important;
  will-change: auto !important;
}

/* Soothing green strip for selected row */
.ag-theme-quartz .ag-row-selected::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: #77c658; /* strip stays green */
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  z-index: 1;
}

.ag-cell-value{
  border-left: unset !important;
  border-right: 1px solid #e5e5e5 !important;
}

.ag-column-first{
  border: unset !important;
}

.ag-theme-quartz .ag-row-selected::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: #77c658; /* strip stays green */
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  z-index: 1;
}

/* Override hover background for selected rows */
.ag-theme-quartz .ag-row-hover .ag-row-selected:hover {
  background-color: #f4fbf1 !important; /* same as selected */
}

.ag-checkbox-input-wrapper::before{
  display: none;
}

.ag-checkbox-input-wrapper::after{
  display: none;
}

  .ag-ltr .ag-sort-indicator-icon {
    padding-left: var(--ag-grid-size);
    display: none;
}

  .ag-header, .ag-advanced-filter-header {
    // background-color: #ff525a;
      background-color: #203342;
    border-bottom: var(--ag-borders-critical) var(--ag-border-color);
}

            .ag-full-width-container{
            height : 100% !important;
          }


         

  
        .ag-header-cell-text {
        font-size: 0.9rem;
      text-transform: uppercase;
      color: #1e374f;
      font-weight: 600;
      font-family: 'Nunito', sans-serif !important;

        }

              .ag-header-cell-resize::after{
                      background-color: #1991eb52;
                  }


            .ag-theme-quartz{
            padding: 0.4rem;
            }

              .ag-ltr .ag-cell{

                      text-align: center;
                      display: flex;
                      // justify-content: center;
                      align-items: center;
              }

              .ag-header{
                  // background-color: #edeff6;
                   border: none;
                  padding: 0.5rem 0;

                  min-height: 44px !important;
              }

              .ag-row {
               border: 1px solid #eeedf0;

              }

                  .ag-row-odd {
    background-color: #fff !important; /* Light gray */
  }

                    .ag-row-even {
    background-color: #f7f7f7 !important; /* Light gray */
  }






              .ag-root-wrapper{
                border: unset;
                background: transparent
              }

              .ag-body-viewport{
                border: 1px solid #8080801a;
                border-top-left-radius: 5px !important;
                border-top-right-radius: 5px !important;
                background-color: white;
              }

              .ag-theme-quartz .ag-header-cell-filter-button{
              display: none;}

.ag-header-cell-resize:after {
    background-color: #1991eb52;
    content: "";
    display: block;
    height: 30%;
    position: absolute;
    top: calc(50% - 30% * .5);2px;
    z-index: 1;
}

               .ag-cell, .ag-cell-not-inline-editing, .ag-cell-normal-height, .ag-cell-value, .ag-cell-focus{
                  font-size: .9rem;
               }

        .ag-body-vertical-scroll{
            display : none;
          }

    .ag-header-cell-sortable .ag-header-cell-label{
           justify-content: start;
        }
        
        .ag-ltr .ag-cell {
            font-weight: 500 !important;
            letter-spacing: 0.5px;
            color: #2b2b2b !important;
        }


          .odd-row {
          background-color: #f8f8fc;
        }
        
        .span-saceuu7u7{
            font-size: 13px; 
            background-color: #FAAB3C; 
            padding: .8rem .5rem;
            border-radius: 10rem;
            height:1rem;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .span-jkmkmkmkm{
            font-size: 10px; 
            color: #6941C6 ;
            border: solid .1px #6941C6; 
            background-color: #F9F5FF; 
            padding: .8rem .5rem;
            border-radius: 10rem;
            width: 7rem;
            height:1rem;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .span-jkmkmkmkmjbui{
            font-size: 10px; 
            color: #175CD3 ;
            border: solid .1px #B2DDFF; 
            background-color: #EFF8FF; 
            padding: .8rem .5rem;
            border-radius: 10rem;
            width: 7rem;
            height:1rem;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
                .ag-header-cell-label{
        display: flex;
        justify-content: center;
        }
         .ag-cell:focus,
  .ag-cell-focus,
  .ag-row-focus {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }

        
        
  
  `;

  let content = null;

  if (!hasData) {
    content = (
      <div className="flex flex-col justify-center items-center h-[30rem]">
        <img
          src="/Images/doctortable.png"
          alt="No Patients"
          className="w-[400px] mb-4"
        />
        <p className="text-muted text-sm">No Patients Available</p>
      </div>
    );
  } else {
    content = (
      <div
        id="grid-wrapper"
        className="ag-theme-quartz custom-ag-grid"
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: "#fff",
          minHeight: "32rem",
        }}
      >
        <style>{tableStyle}</style>
        <AgGridReact
          ref={gridRef}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          rowHeight={60}
          headerHeight={30}
          rowBuffer={0}
          rowModelType={"infinite"}
          cacheBlockSize={100}
          cacheOverflowSize={5}
          maxConcurrentDatasourceRequests={1}
          maxBlocksInCache={10}
          animateRows={true}
          onGridReady={onGridReady}
        />
      </div>
    );
  }

  return <>
  <div style={{ width: "100%", height: "89%" }}>{content}</div>
  <Offcanvas
  show={showOffcanvas}
  onHide={() => setShowOffcanvas(false)}
  placement="end"
  style={{
    width: "70%",
    maxWidth: "none",
    transition: "transform 0.4s ease-in-out",
  }}
>
  <Offcanvas.Header closeButton>
    <Offcanvas.Title>Product Details</Offcanvas.Title>
  </Offcanvas.Header>
  <Offcanvas.Body>
    <PharmacyStock />
  </Offcanvas.Body>
</Offcanvas>

  </>;
}

export default StockComponentTable;
